import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, AlertColor, Slide, SlideProps, Snackbar } from '@mui/material';

interface Toast {
  message: string;
  severity: AlertColor;
}

interface ToastContextValue {
  showToast: (message: string, severity?: AlertColor) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => undefined });

/** App-wide toast. Anchored top-center so it is visible over a tall dialog, not behind it. */
export const useToast = () => useContext(ToastContext);

const SlideDown = (props: SlideProps) => <Slide {...props} direction="down" />;

const AUTO_HIDE_MS = 5000;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((message: string, severity: AlertColor = 'success') => {
    // Drop the current toast first so a second message re-runs the slide-in animation
    // instead of silently swapping the text of a toast already on screen.
    setToast(null);
    setTimeout(() => setToast({ message, severity }), 0);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={!!toast}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideDown}
        // Above MUI's modal layer, so a toast raised from inside a dialog is not hidden by it.
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      >
        <Alert severity={toast?.severity ?? 'success'} variant="filled" onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
